/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Speed Vocab - Timed vocabulary-in-context game
 *
 * SAT Grounding: Craft and Structure domain, "Words in Context" skill.
 * Shows passage excerpts with a question, 4 choices, 15-second timer.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Timer, Trophy, Zap, CheckCircle, XCircle } from 'lucide-react'
import gameService from '../../services/gameService'
import { renderContent } from '../../utils/contentRenderer'
import LoadingSpinner from '../../components/LoadingSpinner'
import { createLogger } from '../../utils/logger'

const logger = createLogger('SpeedVocab')

const ROUND_TIME = 15
const TOTAL_ROUNDS = 10

const SpeedVocab = () => {
  const navigate = useNavigate()
  const [questions, setQuestions] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [score, setScore] = useState(0)
  const [timeLeft, setTimeLeft] = useState(ROUND_TIME)
  const [gameState, setGameState] = useState('loading') // loading, playing, feedback, completed
  const [selectedOption, setSelectedOption] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const timerRef = useRef(null)

  // Load questions
  useEffect(() => {
    const load = async () => {
      try {
        const qs = await gameService.getQuestions('Words in Context', TOTAL_ROUNDS)
        if (qs.length === 0) {
          setError('No vocabulary questions available. Please try again later.')
          return
        }
        setQuestions(qs)
        setGameState('playing')
      } catch (err) {
        setError('Failed to load questions.')
        logger.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // Timer
  useEffect(() => {
    if (gameState !== 'playing') return
    setTimeLeft(ROUND_TIME)
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current)
          handleTimeUp()
          return 0
        }
        return t - 1
      })
    }, 1000)
    return () => clearInterval(timerRef.current)
  }, [currentIndex, gameState])

  const handleTimeUp = useCallback(() => {
    setFeedback({ correct: false, message: "Time's up!" })
    setGameState('feedback')
    setTimeout(() => advance(), 2000)
  }, [currentIndex, questions])

  const handleSelect = useCallback(
    async (optionId) => {
      if (gameState !== 'playing' || selectedOption) return
      clearInterval(timerRef.current)
      setSelectedOption(optionId)

      try {
        const result = await gameService.checkAnswer(questions[currentIndex].question_id, optionId)
        if (result.correct) {
          setScore((s) => s + 1)
          setFeedback({ correct: true, message: 'Correct!' })
        } else {
          setFeedback({
            correct: false,
            message: 'Incorrect',
            correctOptionId: result.correctOptionId
          })
        }
      } catch {
        setFeedback({ correct: false, message: 'Error checking answer' })
      }
      setGameState('feedback')
      setTimeout(() => advance(), 1500)
    },
    [gameState, selectedOption, currentIndex, questions]
  )

  const advance = useCallback(() => {
    if (currentIndex + 1 >= questions.length) {
      setGameState('completed')
    } else {
      setCurrentIndex((i) => i + 1)
      setSelectedOption(null)
      setFeedback(null)
      setGameState('playing')
    }
  }, [currentIndex, questions])

  const restart = useCallback(() => {
    setCurrentIndex(0)
    setScore(0)
    setSelectedOption(null)
    setFeedback(null)
    setGameState('loading')
    setLoading(true)
    gameService
      .getQuestions('Words in Context', TOTAL_ROUNDS)
      .then((qs) => {
        setQuestions(qs)
        setGameState('playing')
      })
      .catch(() => setError('Failed to reload.'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 py-8">
        <button
          onClick={() => navigate('/games')}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Practice Games
        </button>
        <div className="card card-body text-center py-10">
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    )
  }

  if (gameState === 'completed') {
    const accuracy = Math.round((score / questions.length) * 100)
    return (
      <div className="max-w-2xl mx-auto space-y-4 py-8">
        <button
          onClick={() => navigate('/games')}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Practice Games
        </button>
        <div className="card card-body text-center space-y-6 py-10">
          <Trophy className="w-16 h-16 text-yellow-500 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Game Complete!</h2>
          <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {score}/{questions.length}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Correct</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {accuracy}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Accuracy</p>
            </div>
          </div>
          <button
            onClick={restart}
            className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            Play Again
          </button>
        </div>
      </div>
    )
  }

  const question = questions[currentIndex]
  const timerPct = (timeLeft / ROUND_TIME) * 100

  return (
    <div className="max-w-2xl mx-auto space-y-4 py-8">
      <button
        onClick={() => navigate('/games')}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Practice Games
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="w-5 h-5 text-purple-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Question {currentIndex + 1}/{questions.length}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium text-green-600 dark:text-green-400">Score: {score}</span>
          <div className="flex items-center gap-1.5">
            <Timer className={`w-4 h-4 ${timeLeft <= 5 ? 'text-red-500' : ''}`} />
            <span className={timeLeft <= 5 ? 'text-red-500 font-bold' : ''}>{timeLeft}s</span>
          </div>
        </div>
      </div>

      {/* Timer bar */}
      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ${
            timeLeft <= 5 ? 'bg-red-500' : 'bg-primary-500'
          }`}
          style={{ width: `${timerPct}%` }}
        />
      </div>

      {/* Question card */}
      <div className="card card-body space-y-4">
        {question.context && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {renderContent(question.context, question.content_type)}
          </div>
        )}

        <div className="text-gray-900 dark:text-gray-100">
          {renderContent(question.questionText, question.content_type)}
        </div>

        <div className="space-y-2">
          {question.answerOptions.map((opt) => {
            let optStyle =
              'border-gray-200 dark:border-gray-600 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
            if (gameState === 'feedback') {
              if (
                feedback?.correctOptionId === opt.option_id ||
                (feedback?.correct && selectedOption === opt.option_id)
              ) {
                optStyle = 'border-green-500 bg-green-50 dark:bg-green-900/20'
              } else if (selectedOption === opt.option_id && !feedback?.correct) {
                optStyle = 'border-red-500 bg-red-50 dark:bg-red-900/20'
              } else {
                optStyle = 'border-gray-200 dark:border-gray-700 opacity-50'
              }
            } else if (selectedOption === opt.option_id) {
              optStyle = 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            }

            return (
              <button
                key={opt.option_id}
                onClick={() => handleSelect(opt.option_id)}
                disabled={gameState !== 'playing'}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all text-sm ${optStyle}`}
              >
                <div className="flex items-center gap-2">
                  {gameState === 'feedback' &&
                    (feedback?.correctOptionId === opt.option_id ||
                      (feedback?.correct && selectedOption === opt.option_id)) && (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    )}
                  {gameState === 'feedback' &&
                    selectedOption === opt.option_id &&
                    !feedback?.correct && (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                  <span>{renderContent(opt.content, question.content_type)}</span>
                </div>
              </button>
            )
          })}
        </div>

        {feedback && (
          <div
            className={`text-center text-sm font-medium py-2 rounded-lg ${
              feedback.correct
                ? 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20'
                : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20'
            }`}
          >
            {feedback.message}
          </div>
        )}
      </div>
    </div>
  )
}

export default SpeedVocab
